import type { MaxPositionsLimit, PlanId } from '@covered-calls/shared';
import { UNLIMITED } from '@covered-calls/shared';

/**
 * UsageIndicator — shows a tenant's position usage near the create-trade action.
 *
 * Two variants driven by props:
 *   - Limited  (`effectiveLimit` is a finite number): renders "X of N used"
 *     where X = positionsCreated and N = effectiveLimit (Req 10.1).
 *   - Unlimited (`effectiveLimit === UNLIMITED`): renders "X positions" with no
 *     "of N" segment and no cap value (Req 10.2).
 *
 * An accessible text alternative (visually hidden, `.sr-only`) conveys the same
 * count and, for a limited plan, the same limit, associated with the
 * create-trade action so assistive technologies announce it (Req 10.3).
 *
 * Every colour is expressed through `var(--color-*)` design tokens — zero
 * literal colour values (Req 12.3). Class-name styling for `.usage-indicator`
 * lives in global.css (task 14.5).
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4, 12.3
 */

export interface UsageIndicatorProps {
  planId: PlanId;
  effectiveLimit: MaxPositionsLimit;
  positionsCreated: number;
}

export function UsageIndicator({ effectiveLimit, positionsCreated }: UsageIndicatorProps) {
  const isUnlimited = effectiveLimit === UNLIMITED;

  const visibleText = isUnlimited
    ? `${positionsCreated} positions`
    : `${positionsCreated} of ${effectiveLimit} used`;

  const accessibleText = isUnlimited
    ? `${positionsCreated} positions created on an unlimited plan`
    : `${positionsCreated} of ${effectiveLimit} positions used`;

  return (
    <span
      className="usage-indicator"
      style={{ color: 'var(--color-muted)' }}
    >
      <span aria-hidden="true">{visibleText}</span>
      <span className="sr-only">{accessibleText}</span>
    </span>
  );
}
